import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Select, DatePicker, message, Card, Row, Col, Divider } from 'antd';
import axios from 'axios';
import * as serverconfig from '../../serverconn';
import moment from 'moment';

const { Option } = Select;
const { TextArea } = Input;
const dateFormat = 'DD/MM/YYYY';

let token = '';

const DeliveryEditForm = ({ delivery_obj, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [healthCenters, setHealthCenters] = useState([]);

  useEffect(() => {
    if (localStorage.getItem("token")) {
      token = localStorage.getItem("token");
    }

    // Set form initial values
    if (delivery_obj) {
      form.setFieldsValue({
        delivery_number: delivery_obj.delivery_number,
        agent_account: delivery_obj.agent_account,
        pickup_point: delivery_obj.pickup_point,
        destination: delivery_obj.destination,
        delivery_type: delivery_obj.delivery_type,
        delivery_status: delivery_obj.delivery_status,
        delivery_mode: delivery_obj.delivery_mode,
        date: delivery_obj.date ? moment(delivery_obj.date, 'DD/MM/YYYY') : null,
        time_of_pickup: delivery_obj.time_of_pickup,
        time_of_dropoff: delivery_obj.time_of_dropoff,
        delivery_items: delivery_obj.delivery_items,
        received_by_coordinator: delivery_obj.received_by_coordinator,
        received_by: delivery_obj.received_by,
        time_of_receiving: delivery_obj.time_of_receiving,
        receivers_note: delivery_obj.receivers_note
      });
    }

    // Fetch agents, hubs, and health centers
    fetchAgents();
    fetchHubs();
    fetchHealthCenters();
  }, [delivery_obj, form]);

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${serverconfig.backendserverurl}/api/delivery_agents/`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Token ${token}`
        }
      });
      setAgents(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const fetchHubs = async () => {
    try {
      const response = await axios.get(`${serverconfig.backendserverurl}/api/collectionhubs/`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Token ${token}`
        }
      });
      setHubs(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching hubs:', error);
    }
  };

  const fetchHealthCenters = async () => {
    try {
      const response = await axios.get(`${serverconfig.backendserverurl}/api/healthcentres/`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Token ${token}`
        }
      });
      setHealthCenters(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching health centers:', error);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    
    try {
      // Format the date
      const formattedValues = {
        ...values,
        date: values.date ? values.date.format('DD/MM/YYYY') : delivery_obj.date,
        agent_account: values.agent_account || delivery_obj.agent_account
      };

      const response = await axios.post(
        `${serverconfig.backendserverurl}/customqueries/update_umoja_delivery/${delivery_obj.id}`,
        formattedValues,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Token ${token}`
          }
        }
      );

      message.success('Delivery updated successfully!');
      onSuccess();
      
    } catch (error) {
      console.error('Error updating delivery:', error);
      message.error(error.response?.data?.error || 'Failed to update delivery');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title="Edit Delivery Details" style={{ marginBottom: 16 }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          received_by_coordinator: false
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Delivery Number"
              name="delivery_number"
              rules={[{ required: true, message: 'Please enter delivery number' }]}
            >
              <Input placeholder="Enter delivery number" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Delivery Status"
              name="delivery_status"
              rules={[{ required: true, message: 'Please select delivery status' }]}
            >
              <Select placeholder="Select status">
                <Option value="pending">Pending</Option>
                <Option value="delivered">Delivered</Option>
                <Option value="cancelled">Cancelled</Option>
                <Option value="failed">Failed</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Agent"
              name="agent_account"
              rules={[{ required: true, message: 'Please select agent' }]}
            >
              <Select placeholder="Select agent" showSearch>
                {agents.map(agent => (
                  <Option key={agent.id} value={agent.id}>
                    {agent.username}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Delivery Type"
              name="delivery_type"
              rules={[{ required: true, message: 'Please enter delivery type' }]}
            >
              <Input placeholder="Enter delivery type" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Pickup Point"
              name="pickup_point"
              rules={[{ required: true, message: 'Please enter pickup point' }]}
            >
              <Select placeholder="Select pickup point" showSearch>
                {hubs.map(hub => (
                  <Option key={hub.id} value={hub.name}>
                    {hub.name}
                  </Option>
                ))}
                {healthCenters.map(hc => (
                  <Option key={hc.id} value={hc.name}>
                    {hc.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Destination"
              name="destination"
              rules={[{ required: true, message: 'Please enter destination' }]}
            >
              <Select placeholder="Select destination" showSearch>
                {hubs.map(hub => (
                  <Option key={hub.id} value={hub.name}>
                    {hub.name}
                  </Option>
                ))}
                {healthCenters.map(hc => (
                  <Option key={hc.id} value={hc.name}>
                    {hc.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Date"
              name="date"
              rules={[{ required: true, message: 'Please select date' }]}
            >
              <DatePicker 
                format={dateFormat} 
                style={{ width: '100%' }}
                placeholder="Select date"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Time of Pickup"
              name="time_of_pickup"
            >
              <Input placeholder="HH:MM" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Time of Dropoff"
              name="time_of_dropoff"
            >
              <Input placeholder="HH:MM" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Delivery Mode"
              name="delivery_mode"
            >
              <Select placeholder="Select delivery mode">
                <Option value="motorcycle">Motorcycle</Option>
                <Option value="car">Car</Option>
                <Option value="walking">Walking</Option>
                <Option value="bicycle">Bicycle</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Items Total"
              name="items_total"
            >
              <Input type="number" placeholder="Enter items total" />
            </Form.Item>
          </Col>
        </Row>

        <Divider>Receiving Information</Divider>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Received by Coordinator"
              name="received_by_coordinator"
              valuePropName="checked"
            >
              <Select>
                <Option value={true}>Yes</Option>
                <Option value={false}>No</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Received By"
              name="received_by"
            >
              <Input placeholder="Enter receiver name" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Time of Receiving"
              name="time_of_receiving"
            >
              <Input placeholder="HH:MM" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Receiver's Note"
              name="receivers_note"
            >
              <TextArea rows={2} placeholder="Enter receiver's note" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Delivery Items (JSON)"
          name="delivery_items"
        >
          <TextArea 
            rows={4} 
            placeholder="Enter delivery items as JSON"
            style={{ fontFamily: 'monospace' }}
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Button onClick={onCancel} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            Update Delivery
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default DeliveryEditForm;
